import React from 'react'
import {Badge, Box, Button, Popover, Progress, Stack, Table, Text, Tooltip} from '@mantine/core'
import { secondsUntilCopy, type PendingUpload, type UploadState } from '@/protocols/persistence/uploadTypes'
import cx from "clsx";
import commonClasses from "@/common/common.module.css"

interface Props {
    rows: PendingUpload[]
    onRetry: (row: PendingUpload) => void
}

const EMPTY_TESTID = 'upload-rows-empty'

// State-to-label mapping. Mirrors the FileStatusTable vocabulary
// (see FileStatusCell) so the user sees the same wording in both
// surfaces. Collapses transient pre-upload states (copied/pending)
// into a single "Queued" label. 'found' rows show a "Waiting Xs"
// countdown derived from the watcher's min-age gate.
//
// state          | label           | badge color
// ---------------+-----------------+-------------
// found          | Waiting Xs      | blue
// copied         | Queued          | blue
// pending        | Queued          | blue
// uploading      | Uploading (+%)  | green + Progress bar
// persisted      | Just uploaded   | teal (light)
// dead           | failed          | red
//
// The "Uploading" state also renders a Mantine Progress component
// below the badge, mirroring FileStatusCell's progress-bar layout
// so the user can see in-flight upload progress at a glance.
function stateColor(state: UploadState): string {
    switch (state) {
        case 'persisted': return 'teal'
        case 'dead': return 'red'
        case 'uploading': return 'green'
        case 'pending':
        case 'copied':
        case 'found':
            return 'blue'
    }
}

function stateLabel(state: UploadState): string {
    switch (state) {
        case 'found':
        case 'copied':
        case 'pending':
            return 'Queued'
        case 'uploading': return 'Uploading'
        case 'persisted': return 'Just uploaded'
        case 'dead': return 'failed'
    }
}

// Format byte size for human display. Single source of truth (used
// only here) so we can change the formatting (e.g., to IEC units)
// in one place.
function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    const kb = bytes / 1024
    if (kb < 1024) return `${kb.toFixed(1)} KB`
    const mb = kb / 1024
    if (mb < 1024) return `${mb.toFixed(1)} MB`
    return `${(mb / 1024).toFixed(1)} GB`
}

// Stateless presentational table for upload rows. Mirrors
// PendingWritesTable's structure but with upload-specific columns
// (filename, size, State cell). Per user spec, shows ALL states —
// including 'persisted' — so the dialog can highlight what files
// made it to the server.
export const PendingUploadTable: React.FC<Props> = ({rows, onRetry}) => {
    return (
        <Stack gap={4}>
            <Text className={cx(commonClasses.tgbBorder)} size="sm" fw={500}>Pending uploads</Text>
            <Table striped highlightOnHover data-testid="pending-upload-table">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>id</Table.Th>
                        <Table.Th>State</Table.Th>
                        <Table.Th>filename</Table.Th>
                        <Table.Th>size</Table.Th>
                        <Table.Th>last_error</Table.Th>
                        <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.length > 0 ? (
                        rows.map((r) => {
                            const percentage = Math.round((r.uploadProgress ?? 0) * 100)
                            return (
                                <Table.Tr
                                    key={`upload-${r.id}`}
                                    data-testid={`upload-row-${r.id}`}
                                    data-row-kind="upload"
                                    data-upload-state={r.state}
                                >
                                    <Table.Td>{r.id}</Table.Td>
                                    <Table.Td miw={"10em"}>
                                        {r.state === 'uploading' ? (
                                            // Mirrors FileStatusCell: badge +
                                            // inline % + Mantine Progress bar
                                            // below so the user sees the
                                            // percentage at a glance.
                                            <Box>
                                                <Box style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}>
                                                    <Badge
                                                        size="sm"
                                                        color={stateColor(r.state)}
                                                    >
                                                        {stateLabel(r.state)}
                                                    </Badge>
                                                    <Text size="xs"
                                                          style={{fontFamily: "var(--mantine-font-family-fixed)"}}>
                                                        {percentage}%
                                                    </Text>
                                                </Box>
                                                <Progress
                                                    value={percentage}
                                                    color={stateColor(r.state)}
                                                    size="xs"
                                                    mt={4}
                                                    data-testid="upload-progress"
                                                />
                                            </Box>
                                        ) : r.state === 'found' ? (
                                            // 'found' rows haven't been copied yet —
                                            // show a countdown to the watcher's
                                            // copy step. Driven by parent
                                            // re-renders (ProtocolRecorder polls
                                            // every 1 s) so no setInterval is
                                            // needed here. When the age gate has
                                            // passed (or sourceCreatedAt is
                                            // missing), secondsUntilCopy returns 0
                                            // and we fall back to the generic
                                            // 'Queued' badge.
                                            (() => {
                                                const seconds = secondsUntilCopy(r)
                                                if (seconds === 0) {
                                                    return (
                                                        <Badge
                                                            size="sm"
                                                            color={stateColor(r.state)}
                                                            data-testid={`upload-state-${r.id}`}
                                                        >
                                                            {stateLabel(r.state)}
                                                        </Badge>
                                                    )
                                                }
                                                return (
                                                    <Badge
                                                        size="sm"
                                                        color={stateColor(r.state)}
                                                        data-testid={`upload-waiting-${r.id}`}
                                                    >
                                                        Waiting {seconds}s
                                                    </Badge>
                                                )
                                            })()
                                        ) : (
                                            <Badge size="sm" color={stateColor(r.state)} variant={r.state === 'persisted' ? 'light' : 'filled'}>
                                                {stateLabel(r.state)}
                                            </Badge>
                                        )}
                                    </Table.Td>
                                    <Table.Td>
                                        <Text size="sm" title={r.filename}>
                                            {r.filename}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>{formatSize(r.fileSize)}</Table.Td>
                                    <Table.Td>{r.lastError ? r.lastError : '(none)'}</Table.Td>
                                    <Table.Td>
                                        {r.state === 'dead' ? (
                                            <Button
                                                size="xs"
                                                onClick={() => onRetry(r)}
                                                data-testid={`upload-retry-${r.id}`}
                                            >
                                                Retry
                                            </Button>
                                        ) : (
                                            <Text size="sm" c="dimmed">—</Text>
                                        )}
                                    </Table.Td>
                                </Table.Tr>
                            )
                        })
                    ) : (
                        <Table.Tr data-testid={EMPTY_TESTID}>
                            <Table.Td colSpan={6}>
                                <Text size="sm" c="dimmed">
                                    No pending uploads.
                                </Text>
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </Stack>
    )
}
